import type { ReactNode } from "react";

import {
  DocumentEnum,
  MatchDocumentAmountToleranceModeEnum,
  MatchDocumentContactFilterModeEnum,
  MatchDocumentCurrencyModeEnum,
  MatchDocumentDateBasisEnum,
  MatchDocumentDirectionModeEnum,
  MatchDocumentPostingModeEnum,
  MatchDocumentResidualReasonEnum,
  MatchDocumentSettlementBasisEnum,
  MatchDocumentTextMatchModeEnum,
  MatchDocumentUncodedLinePolicyEnum,
  MatchDocumentUndatedDocumentPolicyEnum,
  MatchDocumentUnsummedLinesPolicyEnum,
  type RuleDefinitionSuggestionMatchDocumentFragment,
} from "@/__generated__/graphql";
import {
  NumberWord,
  RecordWord,
  RecordWords,
  Word,
  Words,
  type WordOption,
  type WordRecord,
} from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidget.tools";
import { State } from "@/state";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import {
  AMOUNT_TOLERANCE_MODE_OPTIONS,
  DIRECTION_OPTIONS,
  DOCUMENT_TYPE_OPTIONS,
  MATCH_DATE_BASIS_OPTIONS,
  SETTLEMENT_BASIS_OPTIONS,
} from "./constants/matchDocumentFields";
import type { MatchDocumentField } from "./RuleEditor";

/**
 * The words a setting is said in, where the sentence says it differently from
 * the setting's own name - "look for bills", "still open" - each with the
 * note its menu explains it by.
 */
const OPEN_OR_PAID: WordOption[] = [
  {
    value: "open",
    label: "still open",
    note: "Only documents not yet marked as paid.",
  },
  {
    value: "paid",
    label: "open or already paid",
    note: "Documents marked as paid too - for payments recorded before the bank line arrived.",
  },
];

const AMOUNT_MATTERS: WordOption[] = [
  {
    value: "on",
    label: "must match",
    note: "Only suggest documents whose amount agrees with the payment.",
  },
  {
    value: "off",
    label: "doesn't matter",
    note: "Leave the amount out of the match.",
  },
];

const DATES_MATTER: WordOption[] = [
  {
    value: "on",
    label: "must be close",
    note: "The payment has to land within a window of the document's date.",
  },
  {
    value: "off",
    label: "don't matter",
    note: "Leave the dates out of the match.",
  },
];

const CURRENCY: WordOption[] = [
  {
    value: MatchDocumentCurrencyModeEnum.Same,
    label: "the same",
    note: "Payment and document are in one currency.",
  },
  {
    value: MatchDocumentCurrencyModeEnum.Any,
    label: "different",
    note: "Allow a conversion, against the rate on the payment date.",
  },
];

const UNDATED: WordOption[] = [
  {
    value: MatchDocumentUndatedDocumentPolicyEnum.Skip,
    label: "skip it",
    note: "A document without a date is not suggested.",
  },
  {
    value: MatchDocumentUndatedDocumentPolicyEnum.UploadDate,
    label: "use when it was uploaded",
    note: "Measure from the day the document came in.",
  },
  {
    value: MatchDocumentUndatedDocumentPolicyEnum.Widen,
    label: "widen the window",
    note: "Look further either side of the payment for it.",
  },
];

const DESCRIPTION: WordOption[] = [
  {
    value: MatchDocumentTextMatchModeEnum.Off,
    label: "isn't compared",
    note: "Words on the bank line play no part.",
  },
  {
    value: MatchDocumentTextMatchModeEnum.Reference,
    label: "is compared by reference numbers",
    note: "An invoice or order number on the bank line has to appear on the document.",
  },
  {
    value: MatchDocumentTextMatchModeEnum.Fuzzy,
    label: "is compared by wording",
    note: "The bank line and the document have to share words, allowing for typos and abbreviations.",
  },
  {
    value: MatchDocumentTextMatchModeEnum.Semantic,
    label: "is compared by meaning",
    note: "The bank line and the document have to be about the same thing, however they are worded.",
  },
];

const FROM: WordOption[] = [
  {
    value: MatchDocumentContactFilterModeEnum.Any,
    label: "anyone",
    note: "Any vendor or customer.",
  },
  {
    value: MatchDocumentContactFilterModeEnum.SameAsTransaction,
    label: "whoever the bank line is from",
    note: "The document's contact has to be the payment's.",
  },
  {
    value: MatchDocumentContactFilterModeEnum.Only,
    label: "only",
    note: "Only the contacts picked here.",
  },
  {
    value: MatchDocumentContactFilterModeEnum.Except,
    label: "anyone except",
    note: "Everyone but the contacts picked here.",
  },
];

const RECORDED: WordOption[] = [
  {
    value: MatchDocumentPostingModeEnum.LineItems,
    label: "line by line",
    note: "Each of the document's lines is posted to its own category.",
  },
  {
    value: MatchDocumentPostingModeEnum.ControlAccount,
    label: "as a summary",
    note: "The payment clears the unpaid bills or invoices account in one line.",
  },
  {
    value: MatchDocumentPostingModeEnum.FixedAccount,
    label: "to one account",
    note: "Every match from this rule posts to the account named here.",
  },
];

const UNCODED: WordOption[] = [
  {
    value: MatchDocumentUncodedLinePolicyEnum.AiCode,
    label: "guess one",
    note: "The AI picks a category for the line.",
  },
  {
    value: MatchDocumentUncodedLinePolicyEnum.UseAccount,
    label: "use",
    note: "Every uncategorized line posts to the category named here.",
  },
  {
    value: MatchDocumentUncodedLinePolicyEnum.FallbackControl,
    label: "post it to the unpaid total",
    note: "The line clears the unpaid bills or invoices account.",
  },
  {
    value: MatchDocumentUncodedLinePolicyEnum.Skip,
    label: "don't match",
    note: "Leave the payment for review instead.",
  },
];

const TAX: WordOption[] = [
  {
    value: "copy",
    label: "copy",
    note: "The journal entry includes each line's tax.",
  },
  {
    value: "leave",
    label: "leave out",
    note: "Only the pre-tax line amounts are posted.",
  },
];

const UNSUMMED: WordOption[] = [
  {
    value: MatchDocumentUnsummedLinesPolicyEnum.ResidualAccount,
    label: "book the difference",
    note: "Post the gap between the lines and the total to the difference account.",
  },
  {
    value: MatchDocumentUnsummedLinesPolicyEnum.RelieveControl,
    label: "adjust the unpaid total",
    note: "Clear the gap against the unpaid bills or invoices account.",
  },
  {
    value: MatchDocumentUnsummedLinesPolicyEnum.Skip,
    label: "don't match",
    note: "Leave the payment for review instead.",
  },
];

/** Money out settles what the business owes; money in, what it is owed. */
const DOCUMENTS_FOR_DIRECTION: Partial<
  Record<MatchDocumentDirectionModeEnum, DocumentEnum[]>
> = {
  [MatchDocumentDirectionModeEnum.PaymentsMatchBills]: [
    DocumentEnum.Bill,
    DocumentEnum.PurchaseOrder,
    DocumentEnum.Receipt,
    DocumentEnum.Check,
    DocumentEnum.DebitNote,
  ],
  [MatchDocumentDirectionModeEnum.ReceiptsMatchInvoices]: [
    DocumentEnum.Invoice,
    DocumentEnum.CreditNote,
  ],
};

/**
 * How a bank payment is linked to a document, said as the sentences the
 * rule's "When" is said in: what it looks for, and what it does with what it
 * finds - each setting a live word of a line, and the lines a setting opens up
 * set under it.
 */
export function RuleMatchDocumentAction({
  suggestion,
  readOnly = false,
  onCommit,
  onCommitFields,
}: {
  suggestion: RuleDefinitionSuggestionMatchDocumentFragment;
  readOnly?: boolean;
  onCommit: (field: MatchDocumentField, value: unknown) => void;
  onCommitFields: (
    updates: Partial<Record<MatchDocumentField, unknown>>,
  ) => void;
}) {
  // Accounts and contacts are the rule's business's own; any business may be settled for.
  const entityIdentifier = State.getState().getEntityIdentifier();
  const ownBusiness = entityIdentifier ? [entityIdentifier] : undefined;

  const amountOn = suggestion.amountTolerance != null;
  const datesOn =
    suggestion.dateWindowDaysBefore != null &&
    suggestion.dateWindowDaysAfter != null;
  const lineByLine =
    suggestion.postingMode === MatchDocumentPostingModeEnum.LineItems;
  const settlementBases = (suggestion.settlementBases ?? []).map(String);
  const writeOff = suggestion.residualRules?.find(
    (rule) => rule.reason === MatchDocumentResidualReasonEnum.WriteOff,
  );
  const direction = suggestion.directionMode as
    | MatchDocumentDirectionModeEnum
    | null
    | undefined;
  const documentsForDirection = direction
    ? DOCUMENTS_FOR_DIRECTION[direction]
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div data-testid="rule-find-card">
        <Typography level="title-lg" className="mb-1.5">
          Find
        </Typography>
        <div className="flex flex-col gap-2">
          <Line>
            <Said>Watch</Said>
            <Word
              value={direction ?? ""}
              options={DIRECTION_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
                note: option.description,
              }))}
              isReadOnly={readOnly}
              onChange={(next) => onCommit("directionMode", next)}
            />
            <Said>and look for</Said>
            <Words
              values={(suggestion.documentTypes ?? []).map(String)}
              options={DOCUMENT_TYPE_OPTIONS.filter(
                (option) =>
                  !documentsForDirection ||
                  documentsForDirection.includes(option.value as DocumentEnum),
              ).map((option) => ({
                value: option.value,
                label: `${option.label}s`,
                note: option.description,
              }))}
              placeholder="any document"
              isReadOnly={readOnly}
              onChange={(next) => onCommit("documentTypes", next)}
            />
            <Said>that are</Said>
            <Word
              value={suggestion.includePaid ? "paid" : "open"}
              options={OPEN_OR_PAID}
              isReadOnly={readOnly}
              onChange={(next) => onCommit("includePaid", next === "paid")}
            />
            <Said>, showing up to</Said>
            <NumberWord
              value={suggestion.maxResults}
              isReadOnly={readOnly}
              testId="match-document-maxResults"
              onChange={(next) => onCommit("maxResults", next)}
            />
            <Said>per payment.</Said>
          </Line>
          <Line>
            <Said>The amount</Said>
            <Word
              value={amountOn ? "on" : "off"}
              options={AMOUNT_MATTERS}
              isReadOnly={readOnly}
              onChange={(next) =>
                onCommitFields(
                  next === "on"
                    ? {
                        amountTolerance: 0.01,
                        amountToleranceMode:
                          MatchDocumentAmountToleranceModeEnum.Absolute,
                        settlementBases: [
                          MatchDocumentSettlementBasisEnum.Balance,
                          MatchDocumentSettlementBasisEnum.BalancePartial,
                        ],
                      }
                    : { amountTolerance: null },
                )
              }
            />
            {amountOn && (
              <>
                <Said>the document's</Said>
                <Words
                  values={settlementBases}
                  options={SETTLEMENT_BASIS_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                    note: option.description,
                  }))}
                  isReadOnly={readOnly}
                  onChange={(next) => onCommit("settlementBases", next)}
                />
                <Said>, to within</Said>
                <NumberWord
                  value={suggestion.amountTolerance}
                  step={0.01}
                  isReadOnly={readOnly}
                  testId="match-document-amountTolerance"
                  onChange={(next) => onCommit("amountTolerance", next ?? 0)}
                />
                <Word
                  value={
                    suggestion.amountToleranceMode ??
                    MatchDocumentAmountToleranceModeEnum.Absolute
                  }
                  options={AMOUNT_TOLERANCE_MODE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                    note: option.description,
                  }))}
                  isReadOnly={readOnly}
                  onChange={(next) => onCommit("amountToleranceMode", next)}
                />
              </>
            )}
          </Line>
          {amountOn && (
            <Under>
              <Line>
                <Said>The currencies may be</Said>
                <Word
                  value={
                    suggestion.currencyMode ??
                    MatchDocumentCurrencyModeEnum.Same
                  }
                  options={CURRENCY}
                  isReadOnly={readOnly}
                  onChange={(next) => onCommit("currencyMode", next)}
                />
                {suggestion.currencyMode ===
                  MatchDocumentCurrencyModeEnum.Any && (
                  <>
                    <Said>, with rates up to</Said>
                    <NumberWord
                      value={suggestion.rateTolerance}
                      step={0.1}
                      isReadOnly={readOnly}
                      testId="match-document-rateTolerance"
                      onChange={(next) => onCommit("rateTolerance", next)}
                    />
                    <Said>% apart</Said>
                  </>
                )}
                <Said>.</Said>
              </Line>
              {settlementBases.includes(
                MatchDocumentSettlementBasisEnum.Total,
              ) && (
                <Line>
                  <Said>One payment may clear up to</Said>
                  <NumberWord
                    value={suggestion.maxCombinedDocuments}
                    isReadOnly={readOnly}
                    testId="match-document-maxCombinedDocuments"
                    onChange={(next) => onCommit("maxCombinedDocuments", next)}
                  />
                  <Said>documents at once.</Said>
                </Line>
              )}
            </Under>
          )}

          <Line>
            <Said>The dates</Said>
            <Word
              value={datesOn ? "on" : "off"}
              options={DATES_MATTER}
              isReadOnly={readOnly}
              onChange={(next) =>
                onCommitFields(
                  next === "on"
                    ? {
                        dateWindowDaysBefore: 5,
                        dateWindowDaysAfter: 45,
                        dateWindowDays: null,
                        matchDateBasis: [MatchDocumentDateBasisEnum.Issued],
                      }
                    : {
                        dateWindowDaysBefore: null,
                        dateWindowDaysAfter: null,
                        dateWindowDays: null,
                      },
                )
              }
            />
            {datesOn && (
              <>
                <Said>: the payment lands from</Said>
                <NumberWord
                  value={suggestion.dateWindowDaysBefore}
                  isReadOnly={readOnly}
                  testId="match-document-dateWindowDaysBefore"
                  onChange={(next) =>
                    onCommit("dateWindowDaysBefore", next ?? 0)
                  }
                />
                <Said>days before to</Said>
                <NumberWord
                  value={suggestion.dateWindowDaysAfter}
                  isReadOnly={readOnly}
                  testId="match-document-dateWindowDaysAfter"
                  onChange={(next) =>
                    onCommit("dateWindowDaysAfter", next ?? 0)
                  }
                />
                <Said>days after the document's</Said>
                <Words
                  values={(suggestion.matchDateBasis ?? []).map(String)}
                  options={MATCH_DATE_BASIS_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                    note: option.description,
                  }))}
                  isReadOnly={readOnly}
                  onChange={(next) => onCommit("matchDateBasis", next)}
                />
              </>
            )}
          </Line>
          {datesOn && (
            <Under>
              <Line>
                <Said>A document without a date:</Said>
                <Word
                  value={
                    suggestion.undatedDocumentPolicy ??
                    MatchDocumentUndatedDocumentPolicyEnum.Skip
                  }
                  options={UNDATED}
                  isReadOnly={readOnly}
                  onChange={(next) => onCommit("undatedDocumentPolicy", next)}
                />
                <Said>.</Said>
              </Line>
            </Under>
          )}

          <Line>
            <Said>The description</Said>
            <Word
              value={
                suggestion.textMatchMode ?? MatchDocumentTextMatchModeEnum.Off
              }
              options={DESCRIPTION}
              isReadOnly={readOnly}
              onChange={(next) => onCommit("textMatchMode", next)}
            />
            {suggestion.textMatchMode ===
              MatchDocumentTextMatchModeEnum.Semantic && (
              <>
                <Said>, at a strictness of</Said>
                <NumberWord
                  value={suggestion.similarityThreshold}
                  step={0.05}
                  isReadOnly={readOnly}
                  testId="match-document-similarityThreshold"
                  onChange={(next) => onCommit("similarityThreshold", next)}
                />
              </>
            )}
            <Said>.</Said>
          </Line>

          <Line>
            <Said>It may be from</Said>
            <Word
              value={
                suggestion.contactFilterMode ??
                MatchDocumentContactFilterModeEnum.Any
              }
              options={FROM}
              isReadOnly={readOnly}
              onChange={(next) =>
                onCommitFields(
                  next === MatchDocumentContactFilterModeEnum.Any ||
                    next ===
                      MatchDocumentContactFilterModeEnum.SameAsTransaction
                    ? { contactFilterMode: next, filteredContacts: [] }
                    : { contactFilterMode: next },
                )
              }
            />
            {(suggestion.contactFilterMode ===
              MatchDocumentContactFilterModeEnum.Only ||
              suggestion.contactFilterMode ===
                MatchDocumentContactFilterModeEnum.Except) && (
              <RecordWords
                typename="ContactObject"
                values={(suggestion.filteredContacts ?? []) as WordRecord[]}
                entityIdentifiers={ownBusiness}
                placeholder="these contacts"
                isReadOnly={readOnly}
                testId="match-document-filteredContacts"
                onChange={(next) => onCommit("filteredContacts", next)}
              />
            )}
            <Said>, and belong to</Said>
            <RecordWords
              typename="EntityObject"
              values={(suggestion.settlementEntities ?? []) as WordRecord[]}
              entityIdentifiers={[]}
              placeholder="this business"
              isReadOnly={readOnly}
              testId="match-document-settlementEntities"
              onChange={(next) => onCommit("settlementEntities", next)}
            />
            <Said>.</Said>
          </Line>
        </div>
      </div>

      <div data-testid="rule-apply-card">
        <Typography level="title-lg" className="mb-1.5">
          Apply
        </Typography>
        <div className="flex flex-col gap-2">
          <Line>
            <Said>Record the match</Said>
            <Word
              value={
                suggestion.postingMode ?? MatchDocumentPostingModeEnum.LineItems
              }
              options={RECORDED}
              isReadOnly={readOnly}
              onChange={(next) => onCommit("postingMode", next)}
            />
            {suggestion.postingMode ===
              MatchDocumentPostingModeEnum.FixedAccount && (
              <RecordWord
                typename="ChartedAccountObject"
                value={suggestion.fixedAccount as WordRecord | null}
                entityIdentifiers={ownBusiness}
                placeholder="an account"
                isReadOnly={readOnly}
                testId="match-document-fixedAccount"
                onChange={(next) => onCommit("fixedAccount", next)}
              />
            )}
            <Said>.</Said>
          </Line>
          {lineByLine && (
            <Under>
              <Line>
                <Said>A line without a category:</Said>
                <Word
                  value={
                    suggestion.uncodedLinePolicy ??
                    MatchDocumentUncodedLinePolicyEnum.AiCode
                  }
                  options={UNCODED}
                  isReadOnly={readOnly}
                  onChange={(next) => onCommit("uncodedLinePolicy", next)}
                />
                {suggestion.uncodedLinePolicy ===
                  MatchDocumentUncodedLinePolicyEnum.UseAccount && (
                  <RecordWord
                    typename="ChartedAccountObject"
                    value={suggestion.uncodedAccount as WordRecord | null}
                    entityIdentifiers={ownBusiness}
                    placeholder="a category"
                    isReadOnly={readOnly}
                    testId="match-document-uncodedAccount"
                    onChange={(next) => onCommit("uncodedAccount", next)}
                  />
                )}
                <Said>.</Said>
              </Line>
              <Line>
                <Word
                  value={suggestion.copyLineItemTax ? "copy" : "leave"}
                  options={TAX}
                  isMidSentence={false}
                  isReadOnly={readOnly}
                  onChange={(next) =>
                    onCommit("copyLineItemTax", next === "copy")
                  }
                />
                <Said>each line's tax.</Said>
              </Line>
              <Line>
                <Said>When the lines don't add up to the total,</Said>
                <Word
                  value={
                    suggestion.unsummedLinesPolicy ??
                    MatchDocumentUnsummedLinesPolicyEnum.ResidualAccount
                  }
                  options={UNSUMMED}
                  isReadOnly={readOnly}
                  onChange={(next) => onCommit("unsummedLinesPolicy", next)}
                />
                <Said>.</Said>
              </Line>
            </Under>
          )}
          {amountOn && (
            <Line>
              <Said>Book a difference within the amount's slack to</Said>
              <RecordWord
                typename="ChartedAccountObject"
                value={(writeOff?.chartedAccount ?? null) as WordRecord | null}
                entityIdentifiers={ownBusiness}
                placeholder="an account"
                isReadOnly={readOnly}
                testId="match-document-bookDifferenceAccount"
                onChange={(next) => {
                  if (next == null) {
                    onCommit("residualRules", []);
                    return;
                  }
                  const tolerance =
                    suggestion.amountTolerance == null ||
                    suggestion.amountTolerance === ""
                      ? 0.01
                      : Number(suggestion.amountTolerance);
                  const isPercent =
                    suggestion.amountToleranceMode ===
                    MatchDocumentAmountToleranceModeEnum.Percent;
                  onCommit("residualRules", [
                    {
                      reason: MatchDocumentResidualReasonEnum.WriteOff,
                      tolerance: isPercent ? 0 : tolerance,
                      percentTolerance: isPercent ? tolerance : 0,
                      chartedAccount: next,
                    },
                  ]);
                }}
              />
              <Said>.</Said>
            </Line>
          )}
        </div>
      </div>
    </div>
  );
}

/** One line of the sentence: its words, and its live words among them, wrapping as words wrap. */
function Line({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 min-w-0">
      {children}
    </div>
  );
}

/**
 * Words of the line that are not settings. Punctuation sits against the word
 * before it, as it would in a sentence, rather than a word's gap away.
 */
function Said({ children }: { children: string }) {
  const isPunctuation = /^[.,:;]/.test(children);
  return (
    <Typography
      level="body-md"
      className={cn("text-muted-foreground", isPunctuation && "-ml-1.5")}
    >
      {children}
    </Typography>
  );
}

/** The lines a setting opens up, set under it, ruled off as a group of the where editor is. */
function Under({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 pl-3 [border-left-width:1px] [border-left-style:solid] border-border">
      {children}
    </div>
  );
}
